import { Controller } from "@hotwired/stimulus"

// ApexCharts is loaded on demand, not imported here at the top level: it's
// a ~200KB library used by exactly one screen (the host's results charts),
// and a static import put it in every page's bundle regardless of whether
// that page ever draws a chart. A dynamic import() in render() instead
// makes esbuild split it into its own chunk, fetched only when a chart
// element actually connects.
//
// The host's results charts (the legacy WineFeedbackModal, options and
// colours as they were): the BLIC horizontal bar, the bang-for-buck
// radial, and the aroma / flavor pies. The data rides in on values; the
// chart is drawn when the element is visible, which for a card whose
// details are collapsed is when the collapse opens.
const CHART_CRIMSON = "#9d174d"
const PALETTES = {
  aroma: ["#492090", "#6b2a8f", "#9d174d", "#b8336a", "#8B6CE0"],
  flavor: ["#15803d", "#0f766e", "#b45309", "#7c2d12", "#3f6212"]
}
const THEME_EVENT = "winote:theme-changed"
const CHART_THEMES = {
  light: { text: "#15101C", muted: "#6F6A76", grid: "#CFCBD5", track: "#EAE6EF" },
  dark: { text: "#E1E1E1", muted: "#949197", grid: "#37333E", track: "#26212D" }
}

export default class extends Controller {
  static values = {
    kind: String,
    series: Array,
    labels: Array,
    palette: String,
    noteCount: Number
  }

  connect() {
    this.onThemeChange = (event) => this.applyTheme(event.detail?.theme)
    window.addEventListener(THEME_EVENT, this.onThemeChange)
    if (this.element.offsetParent !== null) {
      this.render()
      return
    }
    this.collapse = this.element.closest(".collapse")
    this.onShown = () => this.render()
    this.collapse?.addEventListener("shown.bs.collapse", this.onShown)
  }

  disconnect() {
    window.removeEventListener(THEME_EVENT, this.onThemeChange)
    this.collapse?.removeEventListener("shown.bs.collapse", this.onShown)
    this.chart?.destroy()
    this.chart = null
  }

  async render() {
    if (this.chart) return
    const { default: ApexCharts } = await import("apexcharts")
    if (this.chart) return
    this.chart = new ApexCharts(this.element, this.options())
    this.chart.render()
  }

  options() {
    const theme = this.resolvedTheme()
    switch (this.kindValue) {
      case "blic": return this.blicOptions(theme)
      case "value": return this.valueOptions(theme)
      default: return this.pieOptions(theme)
    }
  }

  blicOptions(theme) {
    const noteCount = this.noteCountValue
    const themeOptions = this.themeOptions(theme)
    return {
      ...themeOptions,
      chart: { ...themeOptions.chart, type: "bar", height: 210, width: "100%", toolbar: { show: false }, fontFamily: "inherit", redrawOnParentResize: true },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "62%" } },
      colors: [CHART_CRIMSON],
      dataLabels: { enabled: true, formatter: (val) => `${val}%`, style: { fontSize: "14px", colors: ["#fff"] } },
      xaxis: {
        min: 0,
        max: 100,
        tickAmount: 5,
        categories: ["Balance", "Length", "Intensity", "Complexity"],
        labels: { ...themeOptions.xaxis.labels, formatter: (val) => `${Math.round(Number(val))}%` }
      },
      grid: { ...themeOptions.grid, strokeDashArray: 3 },
      legend: { ...themeOptions.legend, show: false },
      tooltip: { ...themeOptions.tooltip, y: { formatter: (val) => `${val}% of ${noteCount} tasters` } },
      series: [{ name: "Tasters", data: this.seriesValue }]
    }
  }

  valueOptions(theme) {
    const noteCount = this.noteCountValue
    const themeOptions = this.themeOptions(theme)
    return {
      ...themeOptions,
      chart: { ...themeOptions.chart, type: "radialBar", height: 300, width: "100%", fontFamily: "inherit", toolbar: { show: false }, parentHeightOffset: 0 },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          bands: [
            { from: 0, to: 30, color: "#ef4444" },
            { from: 30, to: 70, color: "#f59e0b" },
            { from: 70, to: 100, color: "#15803d" }
          ],
          bandsStyle: { strokeWidth: "60%", gap: 2 },
          hollow: { margin: 0, size: "60%" },
          track: { background: CHART_THEMES[theme].track },
          dataLabels: {
            name: { show: false },
            value: { offsetY: 10, color: CHART_THEMES[theme].text, fontSize: "28px", fontWeight: 700, formatter: (val) => `${Math.round(Number(val))}%` }
          }
        }
      },
      fill: { opacity: 0.9 },
      labels: ["Bang for the Buck"],
      tooltip: { ...themeOptions.tooltip, y: { formatter: (val) => `${val}% of ${noteCount} tasters` } },
      series: this.seriesValue
    }
  }

  pieOptions(theme) {
    const themeOptions = this.themeOptions(theme)
    return {
      ...themeOptions,
      chart: { ...themeOptions.chart, type: "pie", height: 320, width: "100%", toolbar: { show: false }, fontFamily: "inherit", redrawOnParentResize: true, parentHeightOffset: 0 },
      colors: PALETTES[this.paletteValue] || PALETTES.aroma,
      labels: this.labelsValue,
      dataLabels: {
        enabled: true,
        style: { fontSize: "11px" },
        formatter: (_val, op) => [
          op?.w.config.labels?.[op.seriesIndex] ?? "",
          String(op?.w.globals.series[op.seriesIndex] ?? "")
        ],
        dropShadow: { enabled: false }
      },
      legend: { ...themeOptions.legend, show: false },
      tooltip: { ...themeOptions.tooltip, y: { formatter: (val) => `${val} mention${val === 1 ? "" : "s"}` } },
      series: this.seriesValue
    }
  }

  resolvedTheme() {
    return document.documentElement.getAttribute("data-bs-theme") === "dark" ? "dark" : "light"
  }

  themeOptions(theme) {
    const colors = CHART_THEMES[theme] || CHART_THEMES.light
    return {
      chart: { foreColor: colors.text },
      theme: { mode: theme },
      grid: { borderColor: colors.grid },
      tooltip: { theme },
      xaxis: { labels: { style: { colors: colors.muted } } },
      yaxis: { labels: { style: { colors: colors.muted } } },
      legend: { labels: { colors: colors.text } }
    }
  }

  applyTheme(theme) {
    if (!this.chart || !CHART_THEMES[theme]) return

    const options = this.themeOptions(theme)
    if (this.kindValue === "value") {
      options.plotOptions = {
        radialBar: {
          track: { background: CHART_THEMES[theme].track },
          dataLabels: { value: { color: CHART_THEMES[theme].text } }
        }
      }
    }
    this.chart.updateOptions(options, false, false)
  }
}
